
  import { mapState } from "vuex"
  import IconCheck from "~/components/icons/IconCheck.vue"
  import PrepareYourDocument from "~/components/id-validator/PrepareYourDocument.vue"
  import UploadDropCard from "@/components/id-validator/UploadDropCard.vue"
  import IDValidationStep from "@/components/id-validator/IDValidationStep.vue"

  // Step constants
  const STEP_SELECT_TYPE = 1
  const STEP_SELECT_COUNTRY = 2
  const STEP_PREPARE_DOCUMENT = 3
  const STEP_UPLOAD_DOCUMENTS = 4
  const STEP_VALIDATE_FRONT = 5
  const STEP_VALIDATE_BACK = 6
  const STEP_SUCCESS = 7

  export default {
    name: `AeqIDValidatorBase`,
    components: {
      IconCheck,
      PrepareYourDocument,
      UploadDropCard,
      IDValidationStep
    },
    props: {
      label: {
        type: String,
        default: ``
      },
      daCode: {
        type: String,
        default: ``
      },
      value: {
        type: Object,
        default: undefined
      },
      errorMessages: {
        type: Array,
        default: () => []
      }
    },
    data() {
      return {
        // Step constants
        STEP_SELECT_TYPE,
        STEP_SELECT_COUNTRY,
        STEP_PREPARE_DOCUMENT,
        STEP_UPLOAD_DOCUMENTS,
        STEP_VALIDATE_FRONT,
        STEP_VALIDATE_BACK,
        STEP_SUCCESS,
        documentTypes: [
          { text: this.$t(`components.id_validator.option.passport`), value: `passport` },
          { text: this.$t(`components.id_validator.option.id_card`), value: `id` }
        ],
        mainCountryCodes: {
          CHE: `/images/countries/ch.png`,
          FRA: `/images/countries/fr.png`,
          DEU: `/images/countries/de.png`,
          ITA: `/images/countries/it.png`,
          LIE: `/images/countries/li.png`
        },
        // Map countries to which side has the MRZ ('front' or 'back')
        countryMrzSide: {
          CHE: "back",
          FRA: "back",
          DEU: "back",
          ITA: "back",
          LIE: "back"
          // Default for other countries is 'back'
        },
        type: `id`,
        document: {
          type: null,
          country: null,
          front: null,
          frontCrop: null,
          back: null,
          backCrop: null,
          mrz: null
        },
        innerValue: this.initValue(this.value),
        startingStep: STEP_SELECT_TYPE,
        step: STEP_SELECT_TYPE,
        timer: null,
        showModal: false,
        cropper: null,
        frontPassed: false,
        backPassed: false,
        filteredProps: {},
        wantedDocType: null,
        showOtherCountries: false,
        isOldDocument: false,
        frontPreview: null,
        backPreview: null,
        frontFile: null,
        backFile: null,
        allowSkip: false
      }
    },
    computed: {
      mrzSide() {
        // Determine which side has MRZ based on country
        const country = this.document.country
        if (this.document.type === "passport") {
          return "front" // Passports always have MRZ on the data page (front)
        }
        // For permits, MRZ is always on the back
        if (this.getDocType() === "permit") {
          return "back"
        }
        // For ID cards, check country mapping (default to 'back' if not specified)
        let side = this.countryMrzSide[country] || "back"

        // For old French or Italian documents, MRZ is on the opposite side
        if (this.isOldDocument && (country === "FRA" || country === "ITA")) {
          side = side === "back" ? "front" : "back"
        }

        return side
      },
      ...mapState({
        countries: (state) => JSON.parse(JSON.stringify(state.formData.countries))
      }),
      mainCountriesList() {
        return Object.keys(this.mainCountryCodes).map((code) => ({
          code,
          url: this.mainCountryCodes[code]
        }))
      },
      isSmallScreen() {
        return this.$vuetify.breakpoint.xsOnly
      },
      token() {
        return this.$router.history.current.query.token || this.$store.state.external.token
      },
      isDisabled() {
        return this.filteredProps?.disabled || false
      },
      translatedErrors() {
        return this.$helpers.wrapInArray(this.errorMessages).map((e) => this.$t(e))
      },
      hasErrors() {
        return this.errorMessages?.length > 0
      },
      canClickNext() {
        if (this.step === STEP_SELECT_TYPE) {
          return this.document.type
        } else if (this.step === STEP_SELECT_COUNTRY) {
          // Skip country selection for permits
          if (this.getDocType() === "permit") {
            return true
          }
          return this.document.country
        } else if (this.step === STEP_PREPARE_DOCUMENT) {
          return true
        } else if (this.step === STEP_UPLOAD_DOCUMENTS) {
          // Check if front is uploaded, and back if ID card
          if (this.document.type === `id`) {
            return this.frontFile && this.backFile
          }
          return this.frontFile
        } else if (this.step === STEP_VALIDATE_FRONT) {
          return this.frontPassed || this.allowSkip
        } else if (this.step === STEP_VALIDATE_BACK) {
          return this.backPassed || this.allowSkip
        }
        return false
      },
      language() {
        return this.$i18n.locale
      },
      showPrevButton() {
        return this.shouldShowPrevButton()
      }
    },
    mounted() {
      const parentObject = { $props: {} }
      this.filteredProps = Object.assign(parentObject.$props, this.$attrs)
      this.filteredProps.label = this.label
    },
    methods: {
      getDocType() {
        throw new Error(`getDocType method must be implemented in the extending component`)
      },
      resetSteps() {
        throw new Error(`resetSteps method must be implemented in the extending component`)
      },
      shouldShowPrevButton() {
        throw new Error(`showPrevButton method must be implemented in the extending component`)
      },
      getDocName(side) {
        throw new Error(`getDocName method must be implemented in the extending component`)
      },
      async onFrontValidationComplete(result) {
        this.document.front = await this.createDocument(result.fullImage, "front")
        this.document.frontCrop = result.crop
        this.document.report = result.report
        this.document.signature = result.signature
        this.frontPassed = true
        setTimeout(() => {
          this.nextStep()
        }, 1000)
      },
      async onBackValidationComplete(result) {
        this.document.back = await this.createDocument(result.fullImage, "back")
        this.document.backCrop = result.crop
        this.document.report = result.report
        this.document.signature = result.signature
        this.backPassed = true
        setTimeout(() => {
          this.nextStep()
        }, 1000)
      },
      initValue(value) {
        if (value) {
          if (!value.new) {
            value.new = []
          }
          if (!value.saved) {
            value.saved = []
          }
          if (!value.report) {
            value.report = ``
          }
          if (!value.signature) {
            value.signature = ``
          }
        } else {
          value = { new: [], saved: [], report: ``, signature: `` }
        }
        return JSON.parse(JSON.stringify(value))
      },
      getDocumentList() {
        let newDocs = []
        if (this.innerValue.new.length !== 0) {
          newDocs = this.innerValue.new
            .filter((d) => d.name.includes(`cropped`) === false)
            .map((f, i) => {
              return {
                sourceIndex: i,
                size: f.size,
                fileName: f.name,
                downloadLink: false,
                deleteLink: false,
                deletable: true,
                data: f.content
              }
            })
        }
        return newDocs.concat(
          this.innerValue.saved
            .filter((d) => d.docType === this.wantedDocType)
            .map((f, i) => ({
              sourceIndex: i,
              ...f
            }))
        )
      },

      selectType(type) {
        this.document.type = type
        this.nextStep()
      },
      selectCountry(code) {
        this.document.country = code
        this.showOtherCountries = false
        this.isOldDocument = false
        if (this.document.type !== `id` || (code !== `FRA` && code !== `ITA`)) {
          this.nextStep()
        }
      },
      toggleOtherCountries() {
        this.showOtherCountries = !this.showOtherCountries
        if (this.showOtherCountries) {
          // Clear country when opening "Other"
          this.document.country = null
        } else {
          // If closing "Other" and no main country selected, clear selection
          if (!Object.keys(this.mainCountryCodes).includes(this.document.country)) {
            this.document.country = null
          }
        }
      },
      onFrontSelected({ file, preview }) {
        this.clearFrontUpload()
        this.frontFile = file
        this.frontPreview = preview
      },
      onBackSelected({ file, preview }) {
        this.clearBackUpload()
        this.backFile = file
        this.backPreview = preview
      },
      clearFrontUpload() {
        this.frontFile = null
        this.frontPreview = null
        this.frontPassed = false
        this.document.frontCrop = null
        this.document.front = null

        try {
          this.$refs.frontUpload.value = ``
        } catch (e) {}
      },
      clearBackUpload() {
        this.backFile = null
        this.backPreview = null
        this.backPassed = false
        this.document.backCrop = null
        this.document.back = null
        try {
          this.$refs.backUpload.value = ``
        } catch (e) {}
      },
      onFrontCropperCleared() {
        this.clearFrontUpload()
        this.step = STEP_UPLOAD_DOCUMENTS
      },
      onBackCropperCleared() {
        this.clearBackUpload()
        this.step = STEP_UPLOAD_DOCUMENTS
      },
      openModal() {
        this.showModal = true
      },
      closeModal() {
        this.showModal = false
        this.resetSteps()
        this.document.type = null
        this.document.country = null
        this.showOtherCountries = false
        this.frontPreview = null
        this.backPreview = null
        this.frontFile = null
        this.backFile = null
        this.frontDragging = false
        this.backDragging = false
        this.innerValue = this.initValue(this.value)
      },
      onCloseModal(isOpen) {},
      submit() {
        this.innerValue.saved = []
        if (this.document.type === `passport`) {
          // Passport only has one side (front)
          this.innerValue.new = [this.document.front]
        } else {
          // ID card has two sides - determine which has MRZ and which doesn't
          this.innerValue.new = [this.document.front, this.document.back]
        }

        this.innerValue.report = this.document.report || ``
        this.innerValue.signature = this.document.signature || ``
        this.innerValue.frontCrop = this.document.frontCrop
        this.innerValue.backCrop = this.document.backCrop
        this.innerValue.mrz = this.mrzSide

        this.$emit(`input`, this.innerValue)
        this.$emit(`change`, this.innerValue)
      },
      nextStep() {
        // Skip step 2 (country selection) for permits
        if (this.step === STEP_SELECT_TYPE && this.getDocType() === "permit") {
          this.step = STEP_PREPARE_DOCUMENT
        } else if (this.step === STEP_VALIDATE_FRONT && this.document.type === `passport`) {
          this.step = STEP_SUCCESS
        } else {
          this.step++
        }
        if (this.step === STEP_SUCCESS) {
          this.submit()
        }
      },
      prevStep() {
        if (this.step === STEP_SUCCESS) {
          if (this.document.type === `passport`) {
            this.step = STEP_VALIDATE_FRONT
          } else {
            this.step = STEP_VALIDATE_BACK
          }
          return
        }
        // Skip step 2 (country selection) for permits when going back
        if (this.step === STEP_PREPARE_DOCUMENT && this.getDocType() === "permit") {
          this.step = STEP_SELECT_TYPE
        } else if (this.step > STEP_SELECT_TYPE) {
          this.step--
        }
      },
      onReady() {
        this.cropper = this.$refs.cropper.cropper
      },
      refreshComponent(doc = null, emitBlur = true, emitInput = true) {
        if (doc) {
          this.innerValue = this.initValue(doc)
          if (emitBlur) {
            this.$emit(`blur`, doc)
          }
        }
        if (emitInput) {
          this.$emit(`input`, this.innerValue)
        }
        this.refreshCounter += 1
      },
      async deleteDocument(doc) {
        try {
          this.$refs.frontValidationStep?.clear()
          this.$refs.backValidationStep?.clear()
          this.clearFrontUpload()
          this.clearBackUpload()
        } catch (error) {
          console.error(`Error clearing croppers:`, error)
        }

        if (doc.deleteLink) {
          const deleteLinkWithoutApi = doc.deleteLink.replace(`/frontoffice`, ``)
          await this.$api.documents
            .delete(deleteLinkWithoutApi)
            .then(() => {
              this.innerValue.saved.splice(doc.sourceIndex, 1)
              this.$flashMessage.success({
                title: `${this.$t(`messages.success`)}`,
                message: this.$t(`messages.document_delete`)
              })
            })
            .catch(() => {
              this.$flashMessage.error({
                title: `<${this.$options.name}> : ${this.$t(`messages.error`)}`,
                message: this.$t(`messages.cant_delete_document`)
              })
            })
            .finally(() => this.refreshComponent())
        } else {
          this.innerValue.new.splice(doc.sourceIndex, 1)
          this.refreshComponent()
        }
      },
      getOldDocumentLabel() {
        if (this.document.country === "FRA") {
          return this.$t("components.id_validator.old_document.france")
        } else if (this.document.country === "ITA") {
          return this.$t("components.id_validator.old_document.italy")
        }
        return ""
      },
      async dataURLSize(dataURL) {
        const res = await fetch(dataURL)
        const blob = await res.blob()
        return blob.size
      },
      async createDocument(dataURL, side, cropped) {
        return {
          size: await this.dataURLSize(dataURL),
          name: this.getDocName(side),
          deletable: false,
          content_type: `image/png`,
          content: dataURL
        }
      }
    }
  }
